import React, { useState, useEffect } from 'react';
import { 
  Clock, Filter, Map, Home, Settings, 
  Trash2, Download, BarChart2, Calendar, Maximize,
  Droplet, Zap, AlertTriangle, CheckCircle, Loader
} from 'lucide-react';

type ActiveTab = 'dashboard' | 'traffic' | 'schedule' | 'zones' | 'settings';

// Mock data types
interface CleaningZone {
  id: string;
  name: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  lastCleaned: Date;
  roomType: string;
  trafficLevel: number;
  dirtAccumulation: number;
  cleaningFrequency: number;
  nextScheduled: Date;
  area: number; // in sq ft
  coordinates: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
}

interface TrafficData {
  timestamp: Date;
  zoneId: string;
  zone: string;
  footsteps: number;
  duration: number; // in seconds
  peakHour: boolean;
}

interface CleaningSchedule {
  id: string;
  zoneName: string;
  zoneId: string;
  startTime: Date;
  duration: number; // in minutes
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'MISSED';
  robotId: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  recurrence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM';
}

interface CleaningStats {
  totalCleaningTime: number; // mins per week
  energySaved: number; // kwh per week
  cleaningEfficiency: number; // percentage
  highTrafficAreas: number;
  scheduledCleanings: number;
  completedCleanings: number;
  missedCleanings: number;
  smartTriggers: number;
  nextCleaning: Date | null;
  robotUtilization: number; // percentage
  mostFrequentZone: string;
  dirtLevels: {
    zoneName: string;
    level: number;
  }[];
}

const AdaptiveCleaningAutomation: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
  const [cleaningZones, setCleaningZones] = useState<CleaningZone[]>([]);
  const [trafficData, setTrafficData] = useState<TrafficData[]>([]);
  const [cleaningSchedule, setCleaningSchedule] = useState<CleaningSchedule[]>([]);
  const [stats, setStats] = useState<CleaningStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [liveMode, setLiveMode] = useState<boolean>(false);
  
  // Helper function to format timestamps as relative time
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const data = generateMockData();
      setCleaningZones(data.zones);
      setTrafficData(data.traffic);
      setCleaningSchedule(data.schedule);
      setStats(data.stats);
      
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Placeholder for generating mock data function to be added later
  const generateMockData = () => {
    // Generate mock cleaning zones
    const zones: CleaningZone[] = [
      {
        id: 'ZONE-1001',
        name: 'Living Room',
        priority: 'HIGH',
        lastCleaned: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
        roomType: 'Common Area',
        trafficLevel: 85,
        dirtAccumulation: 72,
        cleaningFrequency: 5, // times per week
        nextScheduled: new Date(Date.now() + 1000 * 60 * 60 * 12), // 12 hours from now
        area: 320, // sq ft
        coordinates: { x1: 10, y1: 10, x2: 60, y2: 60 }
      },
      {
        id: 'ZONE-1002',
        name: 'Kitchen',
        priority: 'HIGH',
        lastCleaned: new Date(Date.now() - 1000 * 60 * 60 * 12), // 12 hours ago
        roomType: 'High Soil Area',
        trafficLevel: 90,
        dirtAccumulation: 85,
        cleaningFrequency: 7, // times per week
        nextScheduled: new Date(Date.now() + 1000 * 60 * 60 * 24), // 24 hours from now
        area: 180, // sq ft
        coordinates: { x1: 65, y1: 10, x2: 95, y2: 40 }
      },
      {
        id: 'ZONE-1003',
        name: 'Master Bedroom',
        priority: 'MEDIUM',
        lastCleaned: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1), // 1 day ago
        roomType: 'Bedroom',
        trafficLevel: 45,
        dirtAccumulation: 38,
        cleaningFrequency: 3, // times per week
        nextScheduled: new Date(Date.now() + 1000 * 60 * 60 * 36), // 36 hours from now
        area: 210, // sq ft
        coordinates: { x1: 10, y1: 65, x2: 40, y2: 95 }
      },
      {
        id: 'ZONE-1004',
        name: 'Guest Bedroom',
        priority: 'LOW',
        lastCleaned: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3), // 3 days ago
        roomType: 'Bedroom',
        trafficLevel: 15,
        dirtAccumulation: 20,
        cleaningFrequency: 2, // times per week
        nextScheduled: new Date(Date.now() + 1000 * 60 * 60 * 48), // 48 hours from now
        area: 180, // sq ft
        coordinates: { x1: 45, y1: 65, x2: 75, y2: 95 }
      },
      {
        id: 'ZONE-1005',
        name: 'Hallway',
        priority: 'HIGH',
        lastCleaned: new Date(Date.now() - 1000 * 60 * 60 * 18), // 18 hours ago
        roomType: 'Transit Area',
        trafficLevel: 95,
        dirtAccumulation: 68,
        cleaningFrequency: 6, // times per week
        nextScheduled: new Date(Date.now() + 1000 * 60 * 60 * 6), // 6 hours from now
        area: 90, // sq ft
        coordinates: { x1: 50, y1: 40, x2: 60, y2: 65 }
      },
      {
        id: 'ZONE-1006',
        name: 'Bathroom',
        priority: 'MEDIUM',
        lastCleaned: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1.5), // 1.5 days ago
        roomType: 'Wet Area',
        trafficLevel: 60,
        dirtAccumulation: 75,
        cleaningFrequency: 4, // times per week
        nextScheduled: new Date(Date.now() + 1000 * 60 * 60 * 18), // 18 hours from now
        area: 65, // sq ft
        coordinates: { x1: 80, y1: 45, x2: 95, y2: 60 }
      }
    ];

    // Generate traffic data
    const traffic: TrafficData[] = [];
    
    // Generate 50 traffic records
    for (let i = 0; i < 50; i++) {
      const zoneIndex = Math.floor(Math.random() * zones.length);
      const zone = zones[zoneIndex];
      const hourOfDay = Math.floor(Math.random() * 24);
      const isPeakHour = (hourOfDay >= 7 && hourOfDay <= 9) || (hourOfDay >= 17 && hourOfDay <= 19);
      
      traffic.push({
        timestamp: new Date(Date.now() - Math.random() * 86400000 * 3), // Up to 3 days ago
        zoneId: zone.id,
        zone: zone.name,
        footsteps: Math.floor(Math.random() * 100) + (isPeakHour ? 50 : 10),
        duration: Math.floor(Math.random() * 300) + 20, // 20-320 seconds
        peakHour: isPeakHour
      });
    }

    // Sort traffic data by timestamp (newest first)
    traffic.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    // Generate cleaning schedule
    const schedule: CleaningSchedule[] = [];
    
    // Add some scheduled cleanings
    for (let i = 0; i < 10; i++) {
      const zoneIndex = Math.floor(Math.random() * zones.length);
      const zone = zones[zoneIndex];
      const dayOffset = Math.floor(Math.random() * 7); // Up to 7 days in the future
      const hourOffset = Math.floor(Math.random() * 24); // Random hour of the day
      
      const startTime = new Date();
      startTime.setDate(startTime.getDate() + dayOffset);
      startTime.setHours(hourOffset, 0, 0, 0);
      
      const status = 
        startTime.getTime() < Date.now() 
          ? (Math.random() > 0.2 ? 'COMPLETED' : 'MISSED') 
          : (Math.random() > 0.8 ? 'IN_PROGRESS' : 'SCHEDULED');
      
      schedule.push({
        id: `SCHEDULE-${1000 + i}`,
        zoneName: zone.name,
        zoneId: zone.id,
        startTime,
        duration: Math.floor(Math.random() * 30) + 15, // 15-45 minutes
        status,
        robotId: `ROBOT-${100 + Math.floor(Math.random() * 3)}`,
        priority: zone.priority,
        recurrence: ['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM'][Math.floor(Math.random() * 4)] as any
      });
    }
    
    // Sort schedule by start time
    schedule.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    
    // Ensure next scheduled is accurate
    zones.forEach(zone => {
      const nextCleanings = schedule.filter(s => 
        s.zoneId === zone.id && 
        s.status === 'SCHEDULED' &&
        s.startTime.getTime() > Date.now()
      );
      
      if (nextCleanings.length > 0) {
        nextCleanings.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        zone.nextScheduled = nextCleanings[0].startTime;
      }
    });

    // Calculate stats
    const completedCleanings = schedule.filter(s => s.status === 'COMPLETED').length;
    const missedCleanings = schedule.filter(s => s.status === 'MISSED').length;
    const scheduledCleanings = schedule.filter(s => s.status === 'SCHEDULED').length;
    
    const nextCleaning = schedule.find(s => 
      s.status === 'SCHEDULED' && 
      s.startTime.getTime() > Date.now()
    );
    
    // Get zone with highest traffic
    const zoneCounts = traffic.reduce((acc: Record<string, number>, curr) => {
      acc[curr.zone] = (acc[curr.zone] || 0) + curr.footsteps;
      return acc;
    }, {});
    
    const mostFrequentZone = Object.entries(zoneCounts)
      .sort((a, b) => b[1] - a[1])[0][0];
    
    // Stats
    const stats: CleaningStats = {
      totalCleaningTime: completedCleanings * 30, // Assume 30 min avg
      energySaved: Math.floor(Math.random() * 5) + 2, // 2-7 kwh
      cleaningEfficiency: Math.floor(Math.random() * 20) + 75, // 75-95%
      highTrafficAreas: zones.filter(z => z.trafficLevel > 70).length,
      scheduledCleanings,
      completedCleanings,
      missedCleanings,
      smartTriggers: Math.floor(Math.random() * 10) + 5, // 5-15
      nextCleaning: nextCleaning ? nextCleaning.startTime : null,
      robotUtilization: Math.floor(Math.random() * 25) + 65, // 65-90%
      mostFrequentZone,
      dirtLevels: zones.map(zone => ({
        zoneName: zone.name,
        level: zone.dirtAccumulation
      }))
    };

    return { zones, traffic, schedule, stats };
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
        <p className="ml-3 text-gray-600">Loading cleaning automation data...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Adaptive Cleaning Automation</h2>
          <p className="text-gray-600">
            Smart floor sensor-based cleaning optimization and scheduling
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="flex items-center">
            <span className={`mr-2 px-3 py-1 rounded-full text-xs font-medium ${liveMode ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {liveMode ? 'LIVE' : 'STATIC'}
            </span>
            <button
              onClick={() => setLiveMode(!liveMode)}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${
                liveMode ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
              }`}
            >
              {liveMode ? 'Disable Live Updates' : 'Enable Live Updates'}
            </button>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              setTimeout(() => {
                const data = generateMockData();
                setCleaningZones(data.zones);
                setTrafficData(data.traffic);
                setCleaningSchedule(data.schedule);
                setStats(data.stats);
                setLoading(false);
              }, 800);
            }}
            className="flex items-center px-3 py-1 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-200"
          >
            <div className="mr-1">↻</div> Refresh Data
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'dashboard'
                  ? 'text-green-600 border-green-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Home size={18} className="mr-2" />
              Dashboard
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('traffic')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'traffic'
                  ? 'text-green-600 border-green-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <BarChart2 size={18} className="mr-2" />
              Traffic Analysis
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('schedule')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'schedule'
                  ? 'text-green-600 border-green-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Calendar size={18} className="mr-2" />
              Cleaning Schedule
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('zones')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'zones'
                  ? 'text-green-600 border-green-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Map size={18} className="mr-2" />
              Clean Zones
            </button>
          </li>
          <li>
            <button
              onClick={() => setActiveTab('settings')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'settings'
                  ? 'text-green-600 border-green-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Settings size={18} className="mr-2" />
              Settings
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content placeholder - will be implemented in next step */}
      {activeTab === 'dashboard' && stats && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Cleaning Dashboard</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold flex items-center">
                <Calendar className="mr-2 text-green-500" size={18} />
                Next Cleaning
              </h4>
              <div className="mt-2 text-2xl font-bold text-green-600">
                {stats.nextCleaning 
                  ? new Date(stats.nextCleaning).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                  : 'None Scheduled'}
              </div>
              <p className="text-sm text-gray-600">
                {stats.nextCleaning 
                  ? `${new Date(stats.nextCleaning).toLocaleDateString()} - ${cleaningSchedule.find(s => 
                      s.startTime.getTime() === stats.nextCleaning?.getTime())?.zoneName || 'Unknown Zone'}`
                  : 'No upcoming cleanings'}
              </p>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold flex items-center">
                <BarChart2 className="mr-2 text-blue-500" size={18} />
                High Traffic Areas
              </h4>
              <div className="mt-2 text-2xl font-bold text-blue-600">{stats.highTrafficAreas}</div>
              <p className="text-sm text-gray-600">Areas needing frequent cleaning</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold flex items-center">
                <Zap className="mr-2 text-purple-500" size={18} />
                Smart Triggers
              </h4>
              <div className="mt-2 text-2xl font-bold text-purple-600">{stats.smartTriggers}</div>
              <p className="text-sm text-gray-600">Automated cleaning events this week</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold mb-3">Cleaning Efficiency</h4>
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-green-600 bg-green-200">
                      {stats.cleaningEfficiency}% Efficient
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-green-600">
                      Optimized
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-green-200">
                  <div 
                    style={{ width: `${stats.cleaningEfficiency}%` }} 
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
                  ></div>
                </div>
                <div className="text-sm text-gray-600">
                  <p>Based on floor sensor data, cleaning schedules are {stats.cleaningEfficiency}% optimized for actual usage patterns.</p>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold mb-3">Robot Utilization</h4>
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                      {stats.robotUtilization}% Utilized
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-blue-600">
                      Optimized
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200">
                  <div 
                    style={{ width: `${stats.robotUtilization}%` }} 
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                  ></div>
                </div>
                <div className="text-sm text-gray-600">
                  <p>Smart scheduling ensures your robot cleaners are used efficiently based on actual floor sensor data.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Cleaning Schedule Status</h4>
              </div>
              <div className="p-4">
                <div className="flex space-x-3">
                  <div className="flex-1 p-3 bg-blue-50 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Scheduled</div>
                    <div className="text-xl font-bold text-blue-600">{stats.scheduledCleanings}</div>
                  </div>
                  <div className="flex-1 p-3 bg-green-50 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Completed</div>
                    <div className="text-xl font-bold text-green-600">{stats.completedCleanings}</div>
                  </div>
                  <div className="flex-1 p-3 bg-red-50 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Missed</div>
                    <div className="text-xl font-bold text-red-600">{stats.missedCleanings}</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p>Scheduled cleanings are automatically adjusted based on occupancy patterns detected by floor sensors.</p>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Energy & Efficiency</h4>
              </div>
              <div className="p-4">
                <div className="flex space-x-3">
                  <div className="flex-1 p-3 bg-green-50 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Energy Saved</div>
                    <div className="text-xl font-bold text-green-600">{stats.energySaved} kWh</div>
                    <div className="text-xs text-gray-500">This week</div>
                  </div>
                  <div className="flex-1 p-3 bg-purple-50 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Cleaning Time</div>
                    <div className="text-xl font-bold text-purple-600">{stats.totalCleaningTime} min</div>
                    <div className="text-xs text-gray-500">This week</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p>Smart floor sensors optimize robot paths, reducing unnecessary cleaning and saving energy.</p>
                </div>
              </div>
            </div>
          </div>
          
          <h4 className="text-md font-semibold mb-3">Upcoming Cleaning Schedule</h4>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Zone</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Time</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Duration</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Priority</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {cleaningSchedule
                  .filter(schedule => schedule.status === 'SCHEDULED' || schedule.status === 'IN_PROGRESS')
                  .slice(0, 5)
                  .map(schedule => (
                  <tr key={schedule.id}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {schedule.zoneName}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {schedule.startTime.toLocaleString()}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {schedule.duration} min
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        schedule.priority === 'HIGH' ? 'bg-red-100 text-red-800' : 
                        schedule.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-green-100 text-green-800'
                      }`}>
                        {schedule.priority}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        schedule.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {schedule.status === 'IN_PROGRESS' ? (
                          <>
                            <Loader size={12} className="mr-1 animate-spin" />
                            {schedule.status}
                          </>
                        ) : schedule.status}
                      </span>
                    </td>
                  </tr>
                ))}
                
                {cleaningSchedule.filter(s => s.status === 'SCHEDULED' || s.status === 'IN_PROGRESS').length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-sm text-gray-500">
                      No upcoming cleaning scheduled.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {activeTab === 'traffic' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Traffic Analysis</h3>
          
          <p className="text-gray-600 mb-6">
            Smart floor sensors track actual foot traffic patterns to optimize cleaning schedules and focus on high-use areas.
          </p>
          
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-3">High Traffic Zones</h4>
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Zone</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Traffic Level</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Dirt Accumulation</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last Cleaned</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Priority</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {cleaningZones
                    .slice()
                    .sort((a, b) => b.trafficLevel - a.trafficLevel)
                    .map(zone => (
                    <tr key={zone.id}>
                      <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                        {zone.name}
                      </td>
                      <td className="px-3 py-4 text-sm">
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-2">
                            <div 
                              className={`h-2.5 rounded-full ${
                                zone.trafficLevel > 75 ? 'bg-red-500' : 
                                zone.trafficLevel > 50 ? 'bg-yellow-500' : 
                                'bg-green-500'
                              }`}
                              style={{ width: `${zone.trafficLevel}%` }}
                            ></div>
                          </div>
                          <span>{zone.trafficLevel}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm">
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-2">
                            <div 
                              className={`h-2.5 rounded-full ${
                                zone.dirtAccumulation > 75 ? 'bg-red-500' : 
                                zone.dirtAccumulation > 50 ? 'bg-yellow-500' : 
                                'bg-green-500'
                              }`}
                              style={{ width: `${zone.dirtAccumulation}%` }}
                            ></div>
                          </div>
                          <span>{zone.dirtAccumulation}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        {zone.lastCleaned.toLocaleDateString()} ({formatTimeAgo(zone.lastCleaned)})
                      </td>
                      <td className="px-3 py-4 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          zone.priority === 'HIGH' ? 'bg-red-100 text-red-800' : 
                          zone.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-green-100 text-green-800'
                        }`}>
                          {zone.priority}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                <h4 className="font-medium">Traffic Heatmap</h4>
                <div className="flex items-center text-xs text-gray-500">
                  <div className="w-3 h-3 bg-green-300 rounded-sm mr-1"></div>
                  <span className="mr-2">Low</span>
                  <div className="w-3 h-3 bg-yellow-300 rounded-sm mr-1"></div>
                  <span className="mr-2">Medium</span>
                  <div className="w-3 h-3 bg-red-300 rounded-sm mr-1"></div>
                  <span>High</span>
                </div>
              </div>
              <div className="p-4">
                <div className="relative h-64 border border-gray-300 rounded-lg bg-gray-50">
                  {/* Simple visual representation of the floor plan */}
                  <div className="absolute inset-0 m-2">
                    {cleaningZones.map(zone => (
                      <div
                        key={zone.id}
                        className={`absolute rounded-md border ${
                          zone.trafficLevel > 75 ? 'bg-red-200 border-red-300' : 
                          zone.trafficLevel > 50 ? 'bg-yellow-200 border-yellow-300' : 
                          'bg-green-200 border-green-300'
                        }`}
                        style={{
                          left: `${zone.coordinates.x1}%`,
                          top: `${zone.coordinates.y1}%`,
                          width: `${zone.coordinates.x2 - zone.coordinates.x1}%`,
                          height: `${zone.coordinates.y2 - zone.coordinates.y1}%`,
                        }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
                          {zone.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="mt-3 text-sm text-gray-600">
                  This heatmap shows the relative foot traffic across different areas of your home, based on sensor data.
                </p>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Recent Traffic Activity</h4>
              </div>
              <div className="p-4">
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {trafficData.slice(0, 8).map((item, index) => (
                    <div key={index} className="flex items-center p-2 border border-gray-100 rounded-lg">
                      <div className={`flex-shrink-0 w-2 h-10 rounded-full mr-3 ${
                        item.footsteps > 80 ? 'bg-red-400' : 
                        item.footsteps > 40 ? 'bg-yellow-400' : 
                        'bg-green-400'
                      }`}></div>
                      <div className="flex-grow">
                        <div className="font-medium text-sm">{item.zone}</div>
                        <div className="text-gray-500 text-xs">
                          {item.timestamp.toLocaleString()} • {item.peakHour ? 'Peak Hour' : 'Off-Peak'}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className="font-medium text-sm">{item.footsteps} steps</div>
                        <div className="text-gray-500 text-xs">{Math.round(item.duration / 60)} min</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p>
                    Floor sensors detect footsteps, duration, and movement patterns to identify high-traffic times and areas.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h4 className="font-medium">Traffic-Based Cleaning Recommendations</h4>
            </div>
            <div className="divide-y divide-gray-200">
              {cleaningZones
                .slice()
                .sort((a, b) => b.trafficLevel * b.dirtAccumulation - a.trafficLevel * a.dirtAccumulation)
                .slice(0, 3)
                .map(zone => (
                <div key={zone.id} className="p-4 flex items-start">
                  <div className={`flex-shrink-0 rounded-full p-2 mr-3 ${
                    zone.priority === 'HIGH' ? 'bg-red-100' : 
                    zone.priority === 'MEDIUM' ? 'bg-yellow-100' : 
                    'bg-green-100'
                  }`}>
                    <AlertTriangle size={16} className={`${
                      zone.priority === 'HIGH' ? 'text-red-500' : 
                      zone.priority === 'MEDIUM' ? 'text-yellow-500' : 
                      'text-green-500'
                    }`} />
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900">{zone.name} needs attention</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      {zone.trafficLevel > 80 ? 'Very high' : zone.trafficLevel > 60 ? 'High' : 'Moderate'} foot traffic with 
                      {zone.dirtAccumulation > 80 ? ' significant' : zone.dirtAccumulation > 60 ? ' moderate' : ' some'} dirt accumulation.
                      {zone.lastCleaned > new Date(Date.now() - 86400000) 
                        ? ' Was cleaned recently but may need more frequent attention.'
                        : ` Last cleaned ${formatTimeAgo(zone.lastCleaned)}.`
                      }
                    </p>
                    <div className="mt-2">
                      <button className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                        Schedule Cleaning Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'schedule' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Cleaning Schedule</h3>
          
          <p className="text-gray-600 mb-6">
            Smart floor sensors automatically detect optimal cleaning times based on room occupancy patterns and usage data.
          </p>
          
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h4 className="font-medium text-gray-800">Cleaning Calendar</h4>
              <p className="text-sm text-gray-600">Schedule automated cleanings based on optimal vacancy times</p>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-2">
              <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                + Add Cleaning
              </button>
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                <Download size={16} className="mr-1" />
                Export
              </button>
            </div>
          </div>
          
          <div className="mb-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Zone</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Schedule</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Duration</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Recurrence</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Priority</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {cleaningSchedule.map(schedule => (
                  <tr key={schedule.id}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {schedule.zoneName}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {schedule.startTime.toLocaleString()}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {schedule.duration} min
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {schedule.recurrence}
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        schedule.priority === 'HIGH' ? 'bg-red-100 text-red-800' : 
                        schedule.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-green-100 text-green-800'
                      }`}>
                        {schedule.priority}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        schedule.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' : 
                        schedule.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 
                        schedule.status === 'MISSED' ? 'bg-red-100 text-red-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {schedule.status === 'IN_PROGRESS' && (
                          <Loader size={12} className="mr-1 animate-spin" />
                        )}
                        {schedule.status === 'COMPLETED' && (
                          <CheckCircle size={12} className="mr-1" />
                        )}
                        {schedule.status}
                      </span>
                    </td>
                    <td className="py-4 pl-3 pr-4 text-right text-sm font-medium">
                      <button className="text-green-600 hover:text-green-900 mr-3">Edit</button>
                      <button className="text-red-600 hover:text-red-900">Cancel</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Optimal Cleaning Times</h4>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">
                  Based on floor sensor data, these are the best times to schedule cleaning when rooms are typically vacant:
                </p>
                <div className="space-y-4">
                  {cleaningZones.slice(0, 4).map(zone => (
                    <div key={zone.id} className="border border-gray-100 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="font-medium">{zone.name}</h5>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          zone.priority === 'HIGH' ? 'bg-red-100 text-red-800' : 
                          zone.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-green-100 text-green-800'
                        }`}>
                          {zone.priority} Priority
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <div className="bg-blue-50 rounded px-2 py-1 text-xs text-blue-700">
                          ⏰ 6:00 AM - 8:00 AM
                        </div>
                        <div className="bg-blue-50 rounded px-2 py-1 text-xs text-blue-700">
                          ⏰ 10:00 AM - 2:00 PM
                        </div>
                        <div className="bg-blue-50 rounded px-2 py-1 text-xs text-blue-700">
                          ⏰ 8:00 PM - 10:00 PM
                        </div>
                      </div>
                      <button className="mt-2 text-sm text-green-600 hover:text-green-800">
                        Schedule during optimal time →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Occupancy Patterns</h4>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">
                  Floor sensors have detected these typical room occupancy patterns:
                </p>
                <div className="space-y-4">
                  <div className="border border-gray-100 rounded-lg p-3">
                    <h5 className="font-medium mb-2">Living Room</h5>
                    <div className="h-12 bg-gray-100 rounded relative">
                      <div className="absolute inset-y-0 left-0 bg-blue-300 w-1/6"></div>
                      <div className="absolute inset-y-0 left-1/4 bg-blue-500 w-1/6"></div>
                      <div className="absolute inset-y-0 left-1/2 bg-blue-400 w-1/6"></div>
                      <div className="absolute inset-y-0 left-3/4 bg-blue-600 w-1/5"></div>
                      <div className="absolute inset-0 flex items-center justify-between px-2 text-xs text-gray-800">
                        <span>6AM</span>
                        <span>12PM</span>
                        <span>6PM</span>
                        <span>12AM</span>
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500 flex justify-between">
                      <span>Low occupancy 8AM-3PM</span>
                      <span>High occupancy evening</span>
                    </div>
                  </div>
                  
                  <div className="border border-gray-100 rounded-lg p-3">
                    <h5 className="font-medium mb-2">Kitchen</h5>
                    <div className="h-12 bg-gray-100 rounded relative">
                      <div className="absolute inset-y-0 left-1/12 bg-blue-400 w-1/12"></div>
                      <div className="absolute inset-y-0 left-1/4 bg-blue-500 w-1/12"></div>
                      <div className="absolute inset-y-0 left-1/2 bg-blue-600 w-1/12"></div>
                      <div className="absolute inset-y-0 left-2/3 bg-blue-500 w-1/12"></div>
                      <div className="absolute inset-0 flex items-center justify-between px-2 text-xs text-gray-800">
                        <span>6AM</span>
                        <span>12PM</span>
                        <span>6PM</span>
                        <span>12AM</span>
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500 flex justify-between">
                      <span>Peak usage at meal times</span>
                      <span>Low usage between meals</span>
                    </div>
                  </div>
                  
                  <div className="border border-gray-100 rounded-lg p-3">
                    <h5 className="font-medium mb-2">Hallway</h5>
                    <div className="h-12 bg-gray-100 rounded relative">
                      <div className="absolute inset-y-0 left-0 bg-blue-300 w-full"></div>
                      <div className="absolute inset-y-0 left-1/8 bg-blue-400 w-1/4"></div>
                      <div className="absolute inset-y-0 left-3/8 bg-blue-300 w-1/4"></div>
                      <div className="absolute inset-y-0 left-3/4 bg-blue-400 w-1/5"></div>
                      <div className="absolute inset-0 flex items-center justify-between px-2 text-xs text-gray-800">
                        <span>6AM</span>
                        <span>12PM</span>
                        <span>6PM</span>
                        <span>12AM</span>
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500 flex justify-between">
                      <span>Consistent transit throughout day</span>
                      <span>Short durations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h4 className="font-medium">Schedule Recommendations</h4>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Based on your floor sensor data, we recommend the following changes to optimize your cleaning schedule:
              </p>
              <div className="space-y-3">
                <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                  <Droplet className="flex-shrink-0 text-blue-500 mr-3 mt-0.5" size={18} />
                  <div>
                    <h5 className="font-medium text-blue-800">Kitchen needs more frequent cleaning</h5>
                    <p className="text-sm text-blue-700">
                      High foot traffic detected - we recommend increasing from 3x to 7x weekly, scheduled during 2-3PM when the kitchen is typically vacant.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="flex-shrink-0 text-green-500 mr-3 mt-0.5" size={18} />
                  <div>
                    <h5 className="font-medium text-green-800">Guest Bedroom can be cleaned less often</h5>
                    <p className="text-sm text-green-700">
                      Low usage detected - you can reduce from 3x to 1x weekly to save energy and extend robot battery life.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start p-3 bg-purple-50 rounded-lg">
                  <Clock className="flex-shrink-0 text-purple-500 mr-3 mt-0.5" size={18} />
                  <div>
                    <h5 className="font-medium text-purple-800">Living Room scheduling change</h5>
                    <p className="text-sm text-purple-700">
                      Current schedule is during high occupancy time. Recommend moving to 9-11AM when the room is typically vacant.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-center">
                <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                  Apply All Recommendations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'zones' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Clean Zones</h3>
          
          <p className="text-gray-600 mb-6">
            Create and customize cleaning zones based on actual usage data from floor sensors.
          </p>
          
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h4 className="font-medium text-gray-800">Zone Management</h4>
              <p className="text-sm text-gray-600">Define and prioritize areas for targeted cleaning</p>
            </div>
            <div className="mt-4 md:mt-0">
              <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                + Create New Zone
              </button>
            </div>
          </div>
          
          <div className="mb-6">
            <div className="relative h-96 border border-gray-300 rounded-lg bg-gray-50 mb-4">
              {/* Floor plan visualization */}
              {cleaningZones.map(zone => (
                <div
                  key={zone.id}
                  className={`absolute rounded-md border-2 ${
                    zone.priority === 'HIGH' ? 'border-red-500 bg-red-100 bg-opacity-40' : 
                    zone.priority === 'MEDIUM' ? 'border-yellow-500 bg-yellow-100 bg-opacity-40' : 
                    'border-green-500 bg-green-100 bg-opacity-40'
                  }`}
                  style={{
                    left: `${zone.coordinates.x1}%`,
                    top: `${zone.coordinates.y1}%`,
                    width: `${zone.coordinates.x2 - zone.coordinates.x1}%`,
                    height: `${zone.coordinates.y2 - zone.coordinates.y1}%`,
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white bg-opacity-90 rounded px-2 py-1 text-sm font-medium">
                      {zone.name}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="absolute bottom-2 right-2 bg-white rounded-md shadow p-2 text-xs">
                <div className="flex items-center mb-1">
                  <div className="w-3 h-3 bg-red-100 border border-red-500 rounded-sm mr-1"></div>
                  <span>High Priority</span>
                </div>
                <div className="flex items-center mb-1">
                  <div className="w-3 h-3 bg-yellow-100 border border-yellow-500 rounded-sm mr-1"></div>
                  <span>Medium Priority</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-100 border border-green-500 rounded-sm mr-1"></div>
                  <span>Low Priority</span>
                </div>
              </div>
            </div>
            
            <div className="text-center mb-6">
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 mr-2">
                <Maximize size={16} className="mr-1" />
                View Full Map
              </button>
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                <Download size={16} className="mr-1" />
                Export Map
              </button>
            </div>
          </div>
          
          <div className="mb-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Zone Name</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Room Type</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Area</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Traffic</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Priority</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Frequency</th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {cleaningZones.map(zone => (
                  <tr key={zone.id}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {zone.name}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {zone.roomType}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {zone.area} sq ft
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2.5 mr-2">
                          <div 
                            className={`h-2.5 rounded-full ${
                              zone.trafficLevel > 75 ? 'bg-red-500' : 
                              zone.trafficLevel > 50 ? 'bg-yellow-500' : 
                              'bg-green-500'
                            }`}
                            style={{ width: `${zone.trafficLevel}%` }}
                          ></div>
                        </div>
                        <span className="text-xs">{zone.trafficLevel}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        zone.priority === 'HIGH' ? 'bg-red-100 text-red-800' : 
                        zone.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-green-100 text-green-800'
                      }`}>
                        {zone.priority}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {zone.cleaningFrequency}x weekly
                    </td>
                    <td className="py-4 pl-3 pr-4 text-right text-sm font-medium">
                      <button className="text-green-600 hover:text-green-900 mr-3">Edit</button>
                      <button className="text-red-600 hover:text-red-900">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Zone Creation Guidelines</h4>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-500 mt-1 mr-3"></div>
                    <div>
                      <h5 className="font-medium text-gray-900">Use traffic data as a guide</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Create zones based on actual foot traffic patterns detected by your floor sensors, not just room boundaries.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-500 mt-1 mr-3"></div>
                    <div>
                      <h5 className="font-medium text-gray-900">Prioritize high-traffic areas</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Set higher cleaning frequency and priority for areas with consistent foot traffic to maintain cleanliness.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-500 mt-1 mr-3"></div>
                    <div>
                      <h5 className="font-medium text-gray-900">Consider dirt accumulation factors</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Kitchen and entryway areas typically accumulate more dirt and may need higher priority regardless of traffic.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-500 mt-1 mr-3"></div>
                    <div>
                      <h5 className="font-medium text-gray-900">Balance robot battery life</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Create an efficient cleaning path by grouping nearby zones together in your schedule.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Zone Recommendations</h4>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">
                  Based on your floor sensor data, we recommend the following zone adjustments:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                    <AlertTriangle className="flex-shrink-0 text-blue-500 mr-3 mt-0.5" size={18} />
                    <div>
                      <h5 className="font-medium text-blue-800">Consider splitting Living Room</h5>
                      <p className="text-sm text-blue-700">
                        Your living room shows two distinct traffic patterns. Consider creating separate zones for the high-traffic path and the seating area.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                    <AlertTriangle className="flex-shrink-0 text-blue-500 mr-3 mt-0.5" size={18} />
                    <div>
                      <h5 className="font-medium text-blue-800">Kitchen zone priority</h5>
                      <p className="text-sm text-blue-700">
                        Kitchen traffic has increased 35% in the last month. Consider upgrading to HIGH priority with daily cleaning.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                    <AlertTriangle className="flex-shrink-0 text-blue-500 mr-3 mt-0.5" size={18} />
                    <div>
                      <h5 className="font-medium text-blue-800">Add entryway zone</h5>
                      <p className="text-sm text-blue-700">
                        The area just inside your front door shows heavy foot traffic but isn't currently defined as a separate zone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Settings</h3>
          
          <p className="text-gray-600 mb-6">
            Configure your cleaning automation preferences and connected devices.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Connected Cleaning Devices</h4>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <Trash2 size={20} className="text-blue-500" />
                      </div>
                      <div>
                        <h5 className="font-medium">RoboVac Pro 4000</h5>
                        <p className="text-xs text-gray-500">Floor Vacuum • Living Room</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                        Connected
                      </span>
                      <span className="text-xs text-gray-500">Battery: 87%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <Droplet size={20} className="text-blue-500" />
                      </div>
                      <div>
                        <h5 className="font-medium">ScrubMaster 2000</h5>
                        <p className="text-xs text-gray-500">Floor Mop • Kitchen</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                        Connected
                      </span>
                      <span className="text-xs text-gray-500">Battery: 64%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <Trash2 size={20} className="text-blue-500" />
                      </div>
                      <div>
                        <h5 className="font-medium">DustBuster Mini</h5>
                        <p className="text-xs text-gray-500">Corner Vacuum • Bedroom</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">
                        Offline
                      </span>
                      <span className="text-xs text-gray-500">Last seen: 2d ago</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                    + Connect New Device
                  </button>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Automation Preferences</h4>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Auto-schedule based on traffic patterns</span>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input type="checkbox" name="autoSchedule" id="autoSchedule" defaultChecked 
                               className="checked:bg-green-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 appearance-none cursor-pointer" />
                        <label htmlFor="autoSchedule" className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                      </div>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Automatically adjusts cleaning schedule based on floor sensor data.
                    </p>
                  </div>
                  
                  <div>
                    <label className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Prioritize high-traffic areas</span>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input type="checkbox" name="prioritizeTraffic" id="prioritizeTraffic" defaultChecked 
                               className="checked:bg-green-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 appearance-none cursor-pointer" />
                        <label htmlFor="prioritizeTraffic" className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                      </div>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Automatically adjusts cleaning frequency based on traffic levels.
                    </p>
                  </div>
                  
                  <div>
                    <label className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Clean during vacancy windows</span>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input type="checkbox" name="vacancyWindows" id="vacancyWindows" defaultChecked 
                               className="checked:bg-green-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 appearance-none cursor-pointer" />
                        <label htmlFor="vacancyWindows" className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                      </div>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Schedule cleaning when rooms are typically vacant based on historical data.
                    </p>
                  </div>
                  
                  <div>
                    <label className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Notifications for cleaning events</span>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input type="checkbox" name="notifications" id="notifications" defaultChecked 
                               className="checked:bg-green-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 appearance-none cursor-pointer" />
                        <label htmlFor="notifications" className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                      </div>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Receive notifications when cleaning starts or completes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Cleaning Intensity Settings</h4>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default cleaning mode</label>
                    <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                      <option>Standard</option>
                      <option>Deep Clean</option>
                      <option>Quick Clean</option>
                      <option>Quiet Mode</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default cleaning passes</label>
                    <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                      <option>1 Pass</option>
                      <option selected>2 Passes</option>
                      <option>3 Passes</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vacuum power for high-traffic zones</label>
                    <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                      <option>Standard</option>
                      <option selected>High</option>
                      <option>Maximum</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hard floor cleaning mode</label>
                    <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                      <option>Vacuum Only</option>
                      <option selected>Vacuum + Mop</option>
                      <option>Mop Only</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Advanced Settings</h4>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Traffic sensitivity threshold (%)
                    </label>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      defaultValue="70"
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 px-1 mt-1">
                      <span>Low</span>
                      <span>High</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Clean zone re-evaluation frequency
                    </label>
                    <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                      <option>Daily</option>
                      <option selected>Weekly</option>
                      <option>Monthly</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data retention period
                    </label>
                    <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                      <option>30 days</option>
                      <option selected>90 days</option>
                      <option>180 days</option>
                      <option>1 year</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Energy saving mode
                    </label>
                    <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm">
                      <option>Off</option>
                      <option>Balanced</option>
                      <option selected>Maximum Efficiency</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h4 className="font-medium">System Integration</h4>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-sm transition-all cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Home size={20} className="text-blue-500" />
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Connected
                    </span>
                  </div>
                  <h5 className="font-medium">Smart Home Hub</h5>
                  <p className="text-xs text-gray-500 mt-1">
                    Integrate with your home automation system.
                  </p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-sm transition-all cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Zap size={20} className="text-purple-500" />
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Available
                    </span>
                  </div>
                  <h5 className="font-medium">Energy Monitor</h5>
                  <p className="text-xs text-gray-500 mt-1">
                    Track power usage of cleaning devices.
                  </p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-sm transition-all cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Calendar size={20} className="text-green-500" />
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Connected
                    </span>
                  </div>
                  <h5 className="font-medium">Calendar</h5>
                  <p className="text-xs text-gray-500 mt-1">
                    Sync cleaning schedules with your calendar.
                  </p>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end">
                <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdaptiveCleaningAutomation; 